import React, { useState, useEffect, useRef } from 'react';
import { decryptMediaUrl } from '../../utils/mediaEncryption';

interface MessageMediaProps {
  mediaType: 'image' | 'video' | 'link' | 'audio';
  mediaUrl: string;
  thumbnailUrl?: string;
  mediaKey?: string;
  mediaMimeType?: string;
  allowDownload?: boolean; // default true — set false in chat to block saves
  linkPreview?: {
    title?: string;
    description?: string;
    image?: string;
    url?: string;
  };
}

// Shared protection style — blocks right-click, long-press, and drag on media
const noSaveStyle: React.CSSProperties = {
  WebkitTouchCallout: 'none',
  WebkitUserSelect: 'none',
  userSelect: 'none',
  pointerEvents: 'auto'
};

export const MessageMedia: React.FC<MessageMediaProps> = ({
  mediaType,
  mediaUrl,
  thumbnailUrl,
  mediaKey,
  mediaMimeType,
  allowDownload = true,
  linkPreview
}) => {
  const [showFullImage, setShowFullImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>('');

  // Decrypted object URLs — revoked on unmount to avoid memory leaks
  const [decryptedUrl, setDecryptedUrl] = useState<string | null>(null);
  const [decryptedThumb, setDecryptedThumb] = useState<string | null>(null);
  const [decryptError, setDecryptError] = useState(false);
  const objectUrlsRef = useRef<string[]>([]);

  useEffect(() => {
    if (!mediaKey || !mediaMimeType) return;

    let cancelled = false;
    (async () => {
      try {
        const url = await decryptMediaUrl(mediaUrl, mediaKey, mediaMimeType);
        if (cancelled) { URL.revokeObjectURL(url); return; }
        objectUrlsRef.current.push(url);
        setDecryptedUrl(url);

        if (thumbnailUrl && mediaType !== 'audio') {
          const thumb = await decryptMediaUrl(thumbnailUrl, mediaKey, 'image/jpeg');
          if (cancelled) { URL.revokeObjectURL(thumb); return; }
          objectUrlsRef.current.push(thumb);
          setDecryptedThumb(thumb);
        }
      } catch {
        if (!cancelled) setDecryptError(true);
      }
    })();

    return () => {
      cancelled = true;
      objectUrlsRef.current.forEach(u => URL.revokeObjectURL(u));
      objectUrlsRef.current = [];
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaUrl, mediaKey, mediaMimeType]);

  // Resolve the URL to display — prefer decrypted object URL, fall back to raw GCS URL
  const displayUrl   = mediaKey ? (decryptedUrl   ?? null) : mediaUrl;
  const displayThumb = mediaKey ? (decryptedThumb ?? null) : thumbnailUrl;

  // Loading / error states for encrypted media
  if (mediaKey && !decryptedUrl && !decryptError && mediaType !== 'link') {
    return (
      <div style={{ padding: '12px', color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
        🔓 Decrypting…
      </div>
    );
  }
  if (decryptError) {
    return (
      <div style={{ padding: '12px', color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>
        ⚠️ Could not decrypt media
      </div>
    );
  }

  const handleImageClick = (url: string) => {
    setSelectedImage(url);
    setShowFullImage(true);
  };

  const handleDownload = async (url: string, type: 'image' | 'video') => {
    try {
      // Use relative URL for production, full URL for development
      const API_BASE_URL = process.env.NODE_ENV === 'production'
        ? ''
        : (process.env.REACT_APP_API_BASE_URL || '');

      const downloadUrl = `${API_BASE_URL}/api/auth/download-media?url=${encodeURIComponent(url)}&type=${type}`;
      console.log('Downloading from:', downloadUrl);

      const response = await fetch(downloadUrl, {
        credentials: 'include'
      });

      console.log('Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Download error response:', errorText);
        throw new Error(`Download request failed: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      console.log('Blob size:', blob.size, 'type:', blob.type);

      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      const extension = type === 'image' ? 'jpg' : 'mp4';
      link.download = `tapestry-${type}-${Date.now()}.${extension}`;
      link.style.display = 'none';
      document.body.appendChild(link);
      console.log('Triggering download...');
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Download failed:', error);
      alert(`Failed to download ${type}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  if (mediaType === 'audio') {
    return (
      <div style={{ marginTop: '4px', minWidth: '220px' }}>
        <audio
          controls
          src={displayUrl ?? undefined}
          controlsList={allowDownload ? undefined : 'nodownload'}
          onContextMenu={allowDownload ? undefined : e => e.preventDefault()}
          style={{
            width: '100%',
            height: '36px',
            borderRadius: '18px',
            outline: 'none'
          }}
        />
      </div>
    );
  }

  if (mediaType === 'image') {
    return (
      <>
        <div
          style={{
            marginTop: '8px',
            cursor: 'pointer',
            borderRadius: '12px',
            overflow: 'hidden',
            maxWidth: '100%',
            position: 'relative'
          }}
        >
          <img
            src={displayUrl ?? undefined}
            alt="Message attachment"
            draggable={allowDownload}
            onContextMenu={allowDownload ? undefined : e => e.preventDefault()}
            style={{
              width: '100%',
              maxHeight: '400px',
              display: 'block',
              objectFit: 'cover',
              objectPosition: 'center',
              borderRadius: '12px',
              margin: '0 auto',
              ...(allowDownload ? {} : noSaveStyle)
            }}
            onClick={() => handleImageClick(displayUrl ?? mediaUrl)}
          />
          {allowDownload && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDownload(mediaUrl, 'image');
              }}
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                background: 'rgba(0, 0, 0, 0.6)',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'white',
                padding: 0
              }}
              title="Download image"
            >
              <span className="material-icons" style={{ fontSize: '18px' }}>download</span>
            </button>
          )}
        </div>

        {/* Full Image Modal */}
        {showFullImage && (
          <>
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px'
              }}
              onClick={() => setShowFullImage(false)}
            >
              {allowDownload && (
                <button
                  style={{
                    position: 'absolute',
                    top: '20px',
                    right: '80px',
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: 'white'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(selectedImage, 'image');
                  }}
                  title="Download image"
                >
                  <span className="material-icons">download</span>
                </button>
              )}
              <button
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'white'
                }}
                onClick={() => setShowFullImage(false)}
              >
                <span className="material-icons">close</span>
              </button>
              <img
                src={selectedImage}
                alt="Full size"
                draggable={allowDownload}
                onContextMenu={allowDownload ? undefined : e => e.preventDefault()}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  ...(allowDownload ? {} : noSaveStyle)
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </>
        )}
      </>
    );
  }

  if (mediaType === 'video') {
    return (
      <div
        style={{
          marginTop: '8px',
          borderRadius: '12px',
          overflow: 'hidden',
          maxWidth: '100%',
          position: 'relative'
        }}
      >
        <video
          loop
          playsInline
          controls
          controlsList={allowDownload ? undefined : 'nodownload nofullscreen noremoteplayback'}
          disablePictureInPicture={!allowDownload}
          onContextMenu={allowDownload ? undefined : e => e.preventDefault()}
          style={{
            maxWidth: '100%',
            maxHeight: '300px',
            display: 'block',
            borderRadius: '12px',
            ...(allowDownload ? {} : noSaveStyle)
          }}
          poster={displayThumb ?? undefined}
        >
          <source src={displayUrl ?? undefined} type={mediaMimeType || 'video/mp4'} />
          Your browser does not support the video tag.
        </video>
        {allowDownload && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDownload(mediaUrl, 'video');
            }}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              background: 'rgba(0, 0, 0, 0.6)',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white',
              padding: 0
            }}
            title="Download video"
          >
            <span className="material-icons" style={{ fontSize: '18px' }}>download</span>
          </button>
        )}
      </div>
    );
  }

  if (mediaType === 'link' && linkPreview) {
    // Extract YouTube video ID for embedded player
    const isYouTube = linkPreview.url?.includes('youtube.com') || linkPreview.url?.includes('youtu.be');
    let youtubeId = '';

    if (isYouTube && linkPreview.url) {
      const match = linkPreview.url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
      youtubeId = match ? match[1] : '';
    }

    return (
      <a
        href={linkPreview.url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          marginTop: '8px',
          display: 'block',
          textDecoration: 'none',
          border: '1px solid var(--border-color)',
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: 'var(--background-paper)',
          transition: 'transform 0.2s'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.02)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        {isYouTube && youtubeId ? (
          <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
            <iframe
              src={`https://www.youtube.com/embed/${youtubeId}`}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%'
              }}
            />
          </div>
        ) : linkPreview.image ? (
          <img
            src={linkPreview.image}
            alt={linkPreview.title}
            style={{
              width: '100%',
              maxHeight: '200px',
              objectFit: 'cover',
              display: 'block'
            }}
          />
        ) : null}
        <div style={{ padding: '12px' }}>
          {linkPreview.title && (
            <div
              style={{
                fontWeight: '600',
                color: 'var(--text-primary)',
                marginBottom: '4px',
                fontSize: '14px'
              }}
            >
              {linkPreview.title}
            </div>
          )}
          {linkPreview.description && (
            <div
              style={{
                fontSize: '12px',
                color: 'var(--text-secondary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}
            >
              {linkPreview.description}
            </div>
          )}
          {linkPreview.url && (
            <div
              style={{
                fontSize: '11px',
                color: 'var(--primary-color)',
                marginTop: '4px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {new URL(linkPreview.url).hostname}
            </div>
          )}
        </div>
      </a>
    );
  }

  return null;
};
